import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SiteFooter from "@/components/SiteFooter";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQGroup {
  category: string;
  items: FAQItem[];
}

const faqGroups: FAQGroup[] = [
  {
    category: "Buying & Choosing a System",
    items: [
      {
        question: "Wall-mounted or floor-standing split system — which is right for me?",
        answer:
          "It depends on your room layout and how cold your mornings get. Floor-standing units release warm air lower to the ground, which helps eliminate the cold-feet feeling common in Southern Highlands homes during frosty winters. Wall-mounted units suit most bedrooms and living areas. During our on-site assessment we look at insulation, ceiling height, window size and orientation before recommending the right fit for genuine year-round comfort.",
      },
      {
        question: "What size air conditioner do I need for my home?",
        answer:
          "Capacity is measured in kilowatts (kW): roughly 2.5kW for small rooms up to 25m², 3.5kW for 25-35m², 5-7kW for larger living areas, and 8kW+ for open-plan spaces. Because our region sees sub-zero mornings, we size systems on heating capacity as well as cooling — an undersized unit will struggle on the coldest days. We always complete a proper heat-load calculation rather than guessing from floor area alone.",
      },
      {
        question: "Is reverse cycle better than a gas heater for our climate?",
        answer:
          "For most Southern Highlands homes, yes. A modern reverse cycle system extracts heat from outside air even at sub-zero temperatures and delivers three to four times more heating energy than the electricity it consumes, compared with a gas heater burning fuel directly. It also cools in summer, filters air, and needs no flue or gas bottle refills — one system covers both seasons.",
      },
      {
        question: "What's the difference between a split system and ducted air conditioning?",
        answer:
          "A split system heats and cools a single room or open-plan zone via one indoor unit. Ducted systems distribute conditioned air through ceiling vents to multiple rooms from one central unit, with zone control so you only heat or cool the areas in use. Ducted costs more upfront but suits larger homes needing whole-house comfort with a tidy, near-invisible finish.",
      },
      {
        question: "How many split systems can one outdoor unit run?",
        answer:
          "Multi-split outdoor units typically run two to five indoor heads, useful when you want several rooms conditioned without cluttering the exterior of your home with multiple outdoor units. The trade-off is that if the outdoor unit needs servicing, all connected rooms are affected. For larger homes we often compare a multi-split against ducted zoning to find the more cost-effective long-term option.",
      },
      {
        question: "Which brands do you recommend for cold Southern Highlands winters?",
        answer:
          "We install and service Daikin, Mitsubishi Electric, Fujitsu, Samsung, Panasonic and Actron Air — all of which offer cold-climate reverse cycle models rated to maintain heating output well below zero. See our Brands page for model-specific detail. The best brand for you depends on budget, room size and required heating capacity at low outdoor temperatures, which we assess on-site.",
      },
      {
        question: "Should I choose a system with a low-ambient heating rating?",
        answer:
          "Yes, if you're above 600m elevation as much of the Southern Highlands is. Standard units can lose significant heating capacity once outdoor temperatures drop toward zero or below. Cold-climate rated models maintain rated output down to around -15°C, meaning your home stays warm on the coldest Mittagong, Bowral or Robertson mornings instead of the unit struggling or icing up.",
      },
      {
        question: "Do I need a heat-load calculation or is floor area enough?",
        answer:
          "Floor area alone is a rough guide only. Ceiling height, insulation quality, window glazing, orientation, and how exposed a property is to frost and wind all change the real heating and cooling load. We complete a proper assessment on every quote so the unit we recommend is neither undersized (won't cope on frosty mornings) nor oversized (short-cycles and wastes energy).",
      },
    ],
  },
  {
    category: "Installation",
    items: [
      {
        question: "How long does a typical installation take?",
        answer:
          "A single split system is usually installed in half a day to a full day. Multi-split systems and ducted installations generally take one to two days depending on property access, roof space and the number of zones. We confirm an exact timeframe in your written quote after the on-site assessment, so there are no surprises on installation day.",
      },
      {
        question: "Can ducted air conditioning be installed during a new build or renovation?",
        answer:
          "Yes, and it's the most cost-effective time to do it. We work in two stages: rough-in (ductwork, zoning cabling and refrigeration piping) during the framing stage, then fit-off (outdoor unit, controllers and grilles) once walls and ceilings are complete. Installing during construction avoids cutting into finished ceilings later and allows optimal duct routing.",
      },
      {
        question: "Do you install in older homes with brick veneer or heritage features?",
        answer:
          "Yes. The Southern Highlands has many older weatherboard, brick veneer and heritage-style homes, and we regularly work around existing construction, exposed beams, brick cavities and heritage overlays. Where ducted access is limited, we assess whether a bulkhead, split system, or multi-split solution better suits the building without compromising its character.",
      },
      {
        question: "Where should the outdoor unit be positioned in a frost-prone yard?",
        answer:
          "We avoid low-lying areas where frost and cold air pool, and keep the unit clear of gutter run-off and prevailing wind where possible, since ice build-up on the outdoor coil reduces heating efficiency. We also consider noise to neighbours, airflow clearance, and access for future servicing when recommending the mounting position and bracket or slab type.",
      },
      {
        question: "Can I reuse the pipework from my old air conditioner?",
        answer:
          "Usually no. New systems typically require different pipe diameters, refrigerant types and electrical specifications than older units. Existing pipework is also more prone to hidden leaks that reduce performance. We inspect what's there during the assessment and will only reuse infrastructure if it genuinely meets the new system's requirements and passes a pressure test.",
      },
      {
        question: "Will installation damage my walls, roof or garden?",
        answer:
          "Our technicians take care to minimise disruption — protecting flooring, using proper cable and pipe routing, and tidying penetration points with weatherproof sealant and matching finishes where possible. We clean up thoroughly at the end of every job. Any structural or cosmetic considerations are discussed and agreed with you before work begins, not after.",
      },
      {
        question: "Do you supply and install, or just install units I've bought elsewhere?",
        answer:
          "We primarily supply and install as a package, which ensures the unit is correctly matched to your property and comes with full manufacturer warranty support through an authorised dealer. In some cases we can install customer-supplied equipment, though warranty and compatibility should be confirmed with us first — get in touch to discuss your situation.",
      },
    ],
  },
  {
    category: "Heating in Frost & Cold Climate",
    items: [
      {
        question: "Will my reverse cycle system still heat properly during a heavy frost?",
        answer:
          "A correctly sized, cold-climate rated reverse cycle system will continue heating effectively through frosty Southern Highlands mornings. The system automatically runs periodic defrost cycles to clear ice from the outdoor coil, briefly pausing heat output while it does so — this is normal operation, not a fault, and modern units manage it efficiently.",
      },
      {
        question: "Why does my outdoor unit ice up in winter?",
        answer:
          "Some frost or ice on the outdoor coil during cold, humid mornings is normal and is cleared automatically by the unit's defrost cycle. Excessive or persistent ice build-up, however, can indicate low refrigerant, a fan fault, or restricted airflow around the unit — all worth having checked by a licensed technician before the issue affects heating performance.",
      },
      {
        question: "Is it normal for the outdoor unit to steam during defrost?",
        answer:
          "Yes — when the system switches into defrost mode you may see steam or vapour rising from the outdoor unit as built-up ice melts and evaporates. This typically lasts a few minutes before the unit resumes normal heating. It's more noticeable on cold, damp mornings common across Mittagong, Bowral and Moss Vale.",
      },
      {
        question: "How do I stop my heating bill spiking during winter?",
        answer:
          "Set a consistent thermostat temperature (around 20-21°C) rather than cranking it up and down, close doors and curtains overnight to retain warmth, clean filters regularly, and have the system serviced before winter so it isn't working harder than necessary. Correctly sized systems installed with good room sealing use noticeably less energy through a Highlands winter.",
      },
      {
        question: "Can a heat pump keep up with a below-zero morning in Bundanoon or Robertson?",
        answer:
          "A properly sized, cold-climate rated system is designed for exactly these conditions and will maintain comfortable indoor temperatures on sub-zero mornings. The key is correct sizing at the quoting stage — undersized units installed by non-specialists are the main reason some heat pumps struggle in the coldest elevated parts of our service area.",
      },
      {
        question: "Do I need a backup heater alongside my reverse cycle system?",
        answer:
          "Generally no, provided the system was sized correctly for your home's heat load and elevation. A well-matched cold-climate unit is designed to be the sole heating source. If you're relying on an old or undersized system as backup, it's often more cost-effective to upgrade the primary unit than run two systems.",
      },
    ],
  },
  {
    category: "Running Costs & Efficiency",
    items: [
      {
        question: "How much does it cost to run reverse cycle heating overnight?",
        answer:
          "Running costs depend on the unit's efficiency star rating, room size, insulation and set temperature, but modern inverter reverse cycle systems are among the cheapest ways to heat a home per kilowatt of warmth delivered. Using a timer to reduce output overnight rather than leaving the system at full output can further reduce costs without sacrificing comfort.",
      },
      {
        question: "What's the difference between inverter and non-inverter systems for running costs?",
        answer:
          "Inverter systems continuously adjust compressor speed to match the exact heating or cooling demand, rather than switching fully on and off. This uses considerably less energy, wears components more gently, and keeps room temperature steadier — which matters through long Highlands winters where the system may run for many hours at a time.",
      },
      {
        question: "Does a dirty filter really affect running costs?",
        answer:
          "Yes — a clogged filter can increase energy consumption by 5-15% because the system has to work harder to push air through it, and it also reduces indoor air quality. Cleaning filters roughly every two weeks (more often in dusty conditions) is one of the simplest ways to keep both bills and performance in check.",
      },
      {
        question: "Are higher star-rated systems worth the extra upfront cost here?",
        answer:
          "In a cool-climate area where heating runs for months at a time, yes — the efficiency gap between a base-model and a high-efficiency system compounds over a long heating season, often paying back the price difference within a few years. We can compare running-cost estimates for different models during your quote.",
      },
      {
        question: "Does zoning a ducted system actually save money?",
        answer:
          "Yes. Zoning lets you heat or cool only the rooms in use rather than the whole house, which noticeably reduces running costs in larger homes. It's particularly effective in the Highlands where living areas need warming through the day while bedrooms may only need heating in the evening.",
      },
    ],
  },
  {
    category: "Servicing & Maintenance",
    items: [
      {
        question: "How often should my system be serviced?",
        answer:
          "We recommend an annual service, ideally before winter, so heating components are checked and cleaned before the coldest months place the most demand on the system. Commercial or heavily used systems may benefit from six-monthly servicing. Regular servicing catches small issues before they become costly breakdowns during a frosty week.",
      },
      {
        question: "What does an ACG service actually include?",
        answer:
          "A standard service includes filter cleaning, checking refrigerant pressures, inspecting the outdoor coil and fan, testing drainage, checking electrical connections and controls, and confirming the system reaches its rated heating and cooling output. We report any issues found and provide clear recommendations rather than upselling unnecessary work.",
      },
      {
        question: "How often should I clean my own filters at home?",
        answer:
          "Roughly every two weeks under normal use, more often in dusty environments or homes with pets. It's a simple task — most indoor units have a front panel that lifts to reveal a removable filter you can vacuum or rinse. Regular cleaning between professional services keeps efficiency and air quality up.",
      },
      {
        question: "Do you offer a maintenance membership plan?",
        answer:
          "Yes, our membership plan includes scheduled servicing, priority booking, and discounted rates on repairs — useful for homeowners who want to avoid mid-winter breakdowns and keep warranty conditions in good standing. See our Membership page for full inclusions and pricing.",
      },
      {
        question: "Can poor maintenance void my warranty?",
        answer:
          "Most manufacturers require evidence of regular servicing to honour warranty claims, particularly for compressor faults. Skipping services or having unlicensed technicians work on the unit can jeopardise your cover. We provide service records with every visit so you have documentation on hand if a claim is ever needed.",
      },
    ],
  },
  {
    category: "Repairs",
    items: [
      {
        question: "My heating isn't working — what should I check before calling a technician?",
        answer:
          "Check the remote has working batteries, the mode is set to heat rather than cool or fan, filters aren't visibly clogged, and the circuit breaker hasn't tripped. If the outdoor unit is silent or the indoor unit shows an error code, note the code and give us a call — it helps us diagnose the fault faster.",
      },
      {
        question: "Why has my system stopped heating properly but still turns on?",
        answer:
          "Common causes include low refrigerant from a slow leak, a failing outdoor fan motor, blocked filters restricting airflow, or a faulty sensor giving incorrect readings. Ice building up and not clearing during defrost can also point to a fault. A licensed technician can pinpoint the cause safely — refrigerant work must be done by an ARC-licensed professional.",
      },
      {
        question: "Do you offer emergency repairs during a cold snap?",
        answer:
          "We prioritise heating failures during cold weather where we can, understanding how important reliable heating is through a Southern Highlands winter. Call 0480 841 485 to discuss your situation and we'll aim to get a technician to you as quickly as our schedule allows.",
      },
      {
        question: "Is it worth repairing an old system or should I replace it?",
        answer:
          "It depends on the unit's age, the cost of the repair, and how it compares with the cost and efficiency gains of a new system. As a rough guide, if a system is over 10-12 years old and facing a major component failure such as a compressor, replacement is often the better long-term investment. We'll give you an honest comparison, not just a repair quote.",
      },
      {
        question: "Can you service and repair brands other than the ones you install?",
        answer:
          "Yes, our licensed technicians service and repair most major residential and commercial brands, not only the ones we sell. If parts need to be sourced from a manufacturer we don't distribute, we'll advise on likely lead times upfront.",
      },
    ],
  },
  {
    category: "Warranty & Licensing",
    items: [
      {
        question: "What warranty comes with a new system?",
        answer:
          "Manufacturer warranties typically range from 5 to 12 years on parts depending on brand and model, with some offering extended cover when installed and maintained by an authorised dealer. We explain the exact terms for your chosen system at quoting stage, including what's needed to keep the warranty valid.",
      },
      {
        question: "What warranty do you provide on your workmanship?",
        answer:
          "Alongside the manufacturer's parts warranty, our installation workmanship is covered so that any issue arising from the install itself — not general wear or misuse — is rectified at no charge within the warranty period. Full terms are provided in writing with your quote and invoice.",
      },
      {
        question: "Are your technicians licensed to handle refrigerant?",
        answer:
          "Yes. ACG Southern Highlands operates under ARC licence AU38134, and all refrigerant handling — installation, servicing, decommissioning — is carried out by technicians holding current Australian Refrigeration Council accreditation, as required by law for anyone working with refrigerant gases.",
      },
      {
        question: "Are you licensed for the electrical work involved in installation?",
        answer:
          "Yes, all electrical connections associated with your installation are completed to Australian wiring standards by appropriately licensed personnel, and systems are tested and certified as part of commissioning before handover. This protects both your safety and your insurance position.",
      },
      {
        question: "What happens if a part fails within the warranty period?",
        answer:
          "Contact us with your invoice details and a description of the fault. We'll arrange a technician visit to diagnose the issue, and if it's a covered warranty fault we handle the claim with the manufacturer on your behalf and arrange the repair or replacement part.",
      },
    ],
  },
  {
    category: "Finance",
    items: [
      {
        question: "Do you offer payment plans for installations?",
        answer:
          "Yes, we offer flexible finance options including interest-free plans through our finance partners, making it easier to install a properly sized system now rather than delaying through winter. Visit our Finance Options page or ask during your quote for current terms.",
      },
      {
        question: "Is there a minimum spend to use finance?",
        answer:
          "Finance eligibility and minimum amounts depend on the provider and your approved terms. Our team can talk you through what's available for your specific quote — contact us on 0480 841 485 or via the Finance Options page for details.",
      },
      {
        question: "Can I get a fixed quote before deciding on finance?",
        answer:
          "Yes, every job starts with a free, written, fixed-price quote after our on-site assessment. You can then decide whether to pay upfront or apply for a payment plan — there's no obligation either way.",
      },
    ],
  },
  {
    category: "Service Areas",
    items: [
      {
        question: "Which towns do you service?",
        answer:
          "We cover the Southern Highlands and Wollondilly region including Mittagong, Bowral, Moss Vale, Berrima, Burradoo, Bundanoon, Robertson, Goulburn, Picton, Tahmoor, Bargo and Thirlmere, plus surrounding towns. See our Areas We Service page for suburb-specific detail.",
      },
      {
        question: "Do you charge extra for jobs outside Mittagong?",
        answer:
          "Travel is factored into your written quote transparently — there are no hidden call-out surprises. Because we're based centrally in Mittagong, most Highlands towns are a short trip, keeping travel costs reasonable across our service area.",
      },
      {
        question: "Can you service a rural or acreage property?",
        answer:
          "Yes, we regularly work on rural and acreage properties around Robertson, Bundanoon and Goulburn, where longer refrigerant runs, exposed outdoor unit positioning, and wind or frost exposure need extra consideration. We factor these site conditions into the assessment and quote.",
      },
      {
        question: "How quickly can you get to my property for a quote?",
        answer:
          "Booking times vary with seasonal demand, but we aim to schedule on-site assessments promptly, particularly ahead of winter. Use our Schedule a Quote page or call 0480 841 485 and we'll confirm the next available appointment for your area.",
      },
    ],
  },
];

const FAQ = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqGroups.flatMap((group) =>
      group.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      }))
    ),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageSEO
        title="FAQ | Heating, Cooling & AC Questions Answered | ACG Southern Highlands"
        description="40+ answers on choosing, installing, heating through frost, running costs, servicing, repairs, warranty and finance for air conditioning across the Southern Highlands."
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "FAQ" }]} />

      {/* Hero */}
      <section className="bg-acg-charcoal py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-primary-foreground mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-primary-foreground/70 max-w-2xl mx-auto">
            Everything homeowners across Mittagong, Bowral, Moss Vale and the wider Southern Highlands ask us about
            choosing, installing, heating through frost, running costs, servicing, repairs, warranty and finance.
          </p>
        </div>
      </section>

      {/* Category jump links */}
      <section className="bg-background py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {faqGroups.map((group) => (
              <a
                key={group.category}
                href={`#${group.category.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-muted text-muted-foreground hover:bg-muted/80 transition-all"
              >
                {group.category}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Groups — answers always in the DOM as visible text */}
      <section className="py-12 md:py-20 flex-1">
        <div className="container mx-auto px-4 max-w-3xl space-y-16">
          {faqGroups.map((group) => (
            <div
              key={group.category}
              id={group.category.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}
              className="space-y-4"
            >
              <h2 className="text-2xl md:text-3xl font-black text-foreground">{group.category}</h2>
              <div className="space-y-3">
                {group.items.map((item, idx) => (
                  <div key={`${group.category}-${idx}`} className="border border-border rounded-xl bg-card shadow-sm px-6 py-5">
                    <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">{item.question}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default FAQ;
